// bootGate — one boot overlay for the whole mobile launch.
//
// A cold launch of Diamond used to pass through up to three different
// full-screen loading components before the branded overlay took over:
// the auth gate ("Loading Omega ZI AI"), the lazy-chunk Suspense fallback
// ("Loading"), then MobileWorkspace's own overlay ("Connecting to the
// floor"). Each was a fresh mount in a different part of the tree, so the
// logo's entrance animation, the sonar ring and the sweep bar restarted at
// every hand-off — the "loading screen glitches". The overlay also unmounted
// 320 ms after its "done" beat while its CSS fade (240 ms delay + 320 ms)
// had barely started, so it cut out mid-fade.
//
// This tiny external store lets AppRouter own ONE overlay element for the
// entire launch (it sits beside whatever the router renders, so React keeps
// the same DOM node across the auth → lazy-chunk → Diamond hand-offs) while
// DiamondUI keeps reporting its real milestones through MobileWorkspace.
import { useSyncExternalStore } from "react";

// Timing of the exit beats. FILL lets the bar visibly reach 100%; the fade
// itself is CSS (App.css: `.omega-loading-screen--done` transitions opacity
// over BOOT_FADE_MS after BOOT_FADE_DELAY_MS). Unmount only after the fade
// has fully played — a test holds these two files to the same numbers.
export const BOOT_FILL_MS = 260;
export const BOOT_FADE_DELAY_MS = 240;
export const BOOT_FADE_MS = 320;
export const BOOT_UNMOUNT_MS = BOOT_FILL_MS + BOOT_FADE_DELAY_MS + BOOT_FADE_MS + 40;
// Never strand the user behind the overlay if the backend never answers.
// Counted from app start (it now covers the auth round trip too). A warm
// boot from the persisted snapshot fires ready long before this.
export const BOOT_SAFETY_MS = 10000;

export function isDiamondMobileBoot() {
  try {
    if (typeof window === "undefined") return false;
    const mode = localStorage.getItem("zi_mobileUiMode") || "swipe3";
    return mode === "diamond" && window.matchMedia("(max-width: 768px)").matches;
  } catch {
    return false;
  }
}

let state = { active: false, stage: "auth", ready: false };
const listeners = new Set();
const emit = () => listeners.forEach((fn) => { try { fn(); } catch {} });

export const bootGate = {
  get: () => state,
  /** Called once by the router at launch. */
  start(active) { state = { active: !!active, stage: "auth", ready: false }; emit(); },
  setStage(stage) { if (state.active && !state.ready && state.stage !== stage) { state = { ...state, stage }; emit(); } },
  setReady() { if (state.active && !state.ready) { state = { ...state, ready: true }; emit(); } },
  finish() { if (state.active) { state = { ...state, active: false }; emit(); } },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  /** Test helper. */
  _reset() { state = { active: false, stage: "auth", ready: false }; listeners.clear(); },
};

export function useBootGate() {
  return useSyncExternalStore(bootGate.subscribe, bootGate.get, bootGate.get);
}
